import type { Product } from '@/components/delices/types'
import { getMediaUrl } from '@/lib/media'

type ApiImage = { url: string; objectKey: string; position: number }
type ApiCategory = { id: string; name: string; slug: string; description?: string | null; imageUrl?: string | null; imageKey?: string | null }
export type ApiProduct = {
  id: string
  slug: string
  name: string
  description: string
  price: string
  priceTnd?: string
  priceEur?: string
  priceUsd?: string
  createdAt?: string
  currencyCode?: string
  currency?: { code: string; symbol?: string | null }
  weight?: string | null
  origin?: string | null
  published: boolean
  featured: boolean
  bestSeller?: boolean
  rating?: number | null
  category?: ApiCategory | null
  images?: ApiImage[]
}

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || process.env.NEXT_API_PROXY_TARGET || process.env.APP_BASE_URL || 'http://localhost:3000'

async function readServerCurrency() {
  if (typeof window !== 'undefined') return null
  try {
    const { cookies } = await import('next/headers')
    const stored = cookies().get('syris_currency')?.value
    return stored || null
  } catch {
    return null
  }
}

async function resolveCurrency(currency?: string | null) {
  if (currency) return currency
  if (typeof window !== 'undefined') {
    return localStorage.getItem('syris_currency')
  }
  return readServerCurrency()
}

function resolveUrl(path: string) {
  if (path.startsWith('http')) return path
  if (typeof window !== 'undefined') return path
  try {
    return new URL(path, API_BASE).toString()
  } catch {
    return `${API_BASE.replace(/\/$/, '')}${path}`
  }
}

async function request<T>(path: string, opts: RequestInit = {}) {
  const url = resolveUrl(path)
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    cache: 'no-store',
    ...opts,
  })
  const ct = res.headers.get('content-type') || ''
  const body = ct.includes('application/json') ? await res.json().catch(() => ({})) : await res.text()
  return { ok: res.ok, status: res.status, body: body as T }
}

export function mapProduct(api: ApiProduct): Product {
  const gallery = (api.images || [])
    .sort((a, b) => a.position - b.position)
    .map((img) => getMediaUrl(img.objectKey))
    .filter(Boolean)
  const firstImage =
    gallery[0] ||
    'https://images.unsplash.com/photo-1504754524776-8f4f37790ca0?auto=format&w=1200&q=80'
  const currencyCode =
    (api as any).currencyCode ||
    (api as any)?.currency?.code ||
    (typeof (api as any).currency === 'string' ? (api as any).currency : undefined)
  const normalizedCurrency = (currencyCode || 'EUR').toUpperCase()
  const fallbackPrice =
    normalizedCurrency === 'TND'
      ? api.priceTnd
      : normalizedCurrency === 'USD'
        ? api.priceUsd
        : api.priceEur
  const parsedPrice = Number(api.price ?? fallbackPrice ?? 0)
  return {
    id: api.id,
    slug: (api as any).slug || api.id,
    categoryId: api.category?.id,
    name: api.name,
    category: api.category?.name || api.category?.slug || 'produit',
    createdAt: api.createdAt,
    currency: currencyCode,
    price: Number.isNaN(parsedPrice) ? 0 : parsedPrice,
    quantity: (api as any).quantity ?? undefined,
    image: firstImage,
    description: api.description,
    origin: api.origin ?? undefined,
    weight: api.weight ?? undefined,
    gallery: gallery.length ? gallery : [firstImage],
    shipping: api.shipping ?? undefined,
    available: api.published && ((api as any).quantity ?? 0) > 0,
    bestSeller: Boolean((api as any).bestSeller ?? (api as any).featured),
  }
}

export async function getPublicProducts(params?: { page?: number; limit?: number; categoryId?: string; currency?: string }) {
  const search = new URLSearchParams()
  if (params?.page) search.set('page', String(params.page))
  if (params?.limit) search.set('limit', String(params.limit))
  if (params?.categoryId) search.set('categoryId', params.categoryId)
  const currency = await resolveCurrency(params?.currency)
  if (currency) search.set('currency', currency)
  return request<{ products: ApiProduct[]; total: number; pages: number; page: number }>('/api/shop/products' + (search.toString() ? `?${search.toString()}` : ''))
}

export async function getPublicProduct(idOrSlug: string, currency?: string) {
  const search = new URLSearchParams()
  const resolved = await resolveCurrency(currency)
  if (resolved) search.set('currency', resolved)
  return request<{ product: ApiProduct }>(`/api/shop/products/${idOrSlug}${search.toString() ? `?${search.toString()}` : ''}`)
}

export async function getBestSellers(limit = 6, currency?: string) {
  const search = new URLSearchParams({ limit: String(limit) })
  const resolved = await resolveCurrency(currency)
  if (resolved) search.set('currency', resolved)
  return request<{ products: ApiProduct[] }>('/api/shop/products/best-sellers?' + search.toString())
}

export async function getMyOrders() {
  return request<{ orders: any[] }>('/api/shop/my-orders')
}

export async function getMyOrder(id: string) {
  return request<{ order: any }>(`/api/shop/my-orders/${id}`)
}

export async function finalizeDraftOrder(id: string, payload: { buyerFirstName?: string; buyerLastName?: string; buyerPhone: string; shippingAddress: string; billingAddress?: string }) {
  return request<{ order: any }>(`/api/shop/my-orders/${id}/finalize`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export async function getPublicCategories() {
  return request<{ categories: { id: string; name: string; slug: string }[] }>('/api/shop/categories')
}

export async function checkoutOrder(payload: {
  items: { productId: string; quantity: number }[]
  buyerName?: string
  buyerEmail?: string
  buyerPhone?: string
  buyerFirstName?: string
  buyerLastName?: string
  shippingAddress?: string
  billingAddress?: string
  currency?: string
}) {
  return request<{ order: any; error?: string }>('/api/shop/checkout', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export type ApiTestimonial = {
  id: string
  name: string
  role?: string | null
  avatar?: string | null
  rating?: number | null
  text: string
}

export async function getTestimonials() {
  return request<{ testimonials: ApiTestimonial[] }>('/api/shop/testimonials')
}
